import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar, Stars, VerifiedBadge, CategoryIcon, Avatar } from "@/components/shared";
import { ChatSheet } from "@/components/ChatSheet";
import { money } from "@/lib/api";
import { eurEquivalent } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Award, ShieldCheck, Star, Check, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { pickLang } from "@/lib/i18n";

export default function ProfessionalProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const [pro, setPro] = useState(null);
    const [catIcon, setCatIcon] = useState("Wrench");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ description: "", price: "", scheduled_date: "" });
    const [booking, setBooking] = useState(false);

    useEffect(() => {
        api.get(`/professionals/${id}`).then((r) => {
            setPro(r.data);
            setForm((f) => ({ ...f, price: r.data.hourly_rate }));
            api.get("/categories").then((c) => {
                const found = c.data.find((x) => x.slug === r.data.category);
                if (found) setCatIcon(found.icon);
            }).catch(() => {});
        }).catch(() => toast.error(t("Professionista non trovato")));
    }, [id]);

    const startBooking = () => {
        if (!user) return navigate("/auth");
        if (user.role !== "client") return toast.error(t("Solo i clienti possono prenotare"));
        setOpen(true);
    };

    const confirmBooking = async () => {
        setBooking(true);
        try {
            await api.post("/bookings", {
                professional_id: id,
                category: pro.category,
                description: form.description,
                price: parseFloat(form.price),
                scheduled_date: form.scheduled_date || null,
            });
            toast.success(t("Richiesta di prenotazione inviata!"));
            setOpen(false);
            navigate("/cliente");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBooking(false);
        }
    };

    if (!pro)
        return (
            <div className="min-h-screen bg-cream">
                <Navbar />
                <div className="py-32 flex justify-center">
                    <div className="h-8 w-8 rounded-full border-2 border-terracotta border-t-transparent animate-spin" />
                </div>
            </div>
        );

    return (
        <div className="min-h-screen bg-cream">
            <Navbar />
            <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10 grid lg:grid-cols-3 gap-8">
                {/* MAIN */}
                <div className="lg:col-span-2">
                    <div className="rounded-3xl overflow-hidden border border-border bg-white">
                        <div className="aspect-[16/9] overflow-hidden">
                            <Avatar src={pro.photo_url} name={pro.name} className="h-full w-full" fallbackClassName="text-6xl" />
                        </div>
                        <div className="p-7">
                            <div className="flex flex-wrap items-center gap-3">
                                <h1 className="font-heading text-3xl font-black text-ink">{pro.name}</h1>
                                {pro.verified && <VerifiedBadge />}
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-ink/70">
                                <span className="inline-flex items-center gap-1.5 text-terracotta font-medium">
                                    <CategoryIcon name={catIcon} size={16} /> {pro.category_name}
                                </span>
                                <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{pro.city}</span>
                                {pro.experience_years ? <span className="inline-flex items-center gap-1"><Award className="h-4 w-4" />{pro.experience_years} anni di esperienza</span> : null}
                                <Stars value={pro.rating} count={pro.reviews_count} />
                            </div>
                            <p className="mt-6 text-ink/70 leading-relaxed">{pro.bio}</p>

                            {pro.skills?.length > 0 && (
                                <div className="mt-6">
                                    <p className="uppercase text-xs tracking-[0.2em] font-medium text-ink/50 mb-3">Competenze</p>
                                    <div className="flex flex-wrap gap-2">
                                        {pro.skills.map((s) => (
                                            <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-creamalt border border-border px-3 py-1.5 text-sm font-medium text-ink">
                                                <Check className="h-3.5 w-3.5 text-terracotta" />{s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* PORTFOLIO */}
                    {pro.portfolio?.length > 0 && (
                        <div className="mt-8">
                            <h2 className="font-heading text-2xl font-bold text-ink">Lavori svolti</h2>
                            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {pro.portfolio.map((url, i) => (
                                    <div key={i} className="aspect-square rounded-2xl overflow-hidden border border-border" data-testid={`portfolio-${i}`}>
                                        <img src={url} alt={`Lavoro ${i + 1}`} className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* REVIEWS */}
                    <div className="mt-8">
                        <h2 className="font-heading text-2xl font-bold text-ink flex items-center gap-2">
                            <Star className="h-5 w-5 fill-terracotta text-terracotta" /> Recensioni ({pro.reviews_count})
                        </h2>
                        <div className="mt-4 space-y-4">
                            {pro.reviews?.length === 0 && <p className="text-ink/50">Ancora nessuna recensione.</p>}
                            {pro.reviews?.map((r) => (
                                <div key={r.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`review-${r.id}`}>
                                    <div className="flex items-center justify-between">
                                        <p className="font-semibold text-ink">{r.client_name || "Cliente"}</p>
                                        <span className="inline-flex items-center gap-1">
                                            {Array.from({ length: r.rating }).map((_, i) => (
                                                <Star key={i} className="h-4 w-4 fill-terracotta text-terracotta" />
                                            ))}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-ink/70">{r.comment}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* SIDEBAR / BOOKING */}
                <div className="lg:col-span-1">
                    <div className="sticky top-24 rounded-3xl border border-border bg-white p-6">
                        <p className="text-ink/60 text-sm">Tariffa oraria</p>
                        <p className="font-heading text-4xl font-black text-ink mt-1">{money(pro.hourly_rate, pro.currency_symbol)}<span className="text-lg font-normal text-ink/50">/ora</span></p>
                        {eurEquivalent(pro.hourly_rate, pro.currency_code) != null && (
                            <p className="text-xs text-ink/50" data-testid="eq-price">≈ €{eurEquivalent(pro.hourly_rate, pro.currency_code)}/ora (stima)</p>
                        )}
                        {pro.service_radius_km ? (
                            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-ink/70" data-testid="service-radius">
                                <MapPin className="h-4 w-4 text-terracotta" /> Si sposta fino a {pro.service_radius_km} km da {pro.city}
                            </p>
                        ) : null}

                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button onClick={(e) => { e.preventDefault(); startBooking(); }} className="mt-5 w-full h-12 bg-terracotta hover:bg-terracottahover rounded-full text-base font-semibold" data-testid="book-btn">
                                    Prenota ora
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-2xl">Prenota {pro.name}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-2">
                                    <div>
                                        <Label htmlFor="desc">Descrivi il lavoro</Label>
                                        <Textarea id="desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                                            placeholder="Es. Riparazione perdita sotto il lavello…" className="mt-1.5" data-testid="booking-desc" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label htmlFor="price">Budget ({pro.currency_symbol})</Label>
                                            <Input id="price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" data-testid="booking-price" />
                                        </div>
                                        <div>
                                            <Label htmlFor="date">Data</Label>
                                            <Input id="date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="mt-1.5" data-testid="booking-date" />
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button onClick={confirmBooking} disabled={booking || !form.description} className="w-full bg-ink hover:bg-inkhover rounded-full" data-testid="confirm-booking">
                                        {booking ? "Invio…" : "Invia richiesta"}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        {user?.role === "client" ? (
                            <ChatSheet startWithProfessionalId={id}>
                                <Button variant="outline" className="mt-3 w-full h-11 rounded-full border-ink/20 font-semibold" data-testid="contact-btn">
                                    <MessageCircle className="h-4 w-4 mr-1" /> Contatta
                                </Button>
                            </ChatSheet>
                        ) : !user ? (
                            <Button variant="outline" onClick={() => navigate("/auth")} className="mt-3 w-full h-11 rounded-full border-ink/20 font-semibold" data-testid="contact-btn">
                                <MessageCircle className="h-4 w-4 mr-1" /> Contatta
                            </Button>
                        ) : null}

                        <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
                            <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                            <p className="text-sm text-emerald-900">Il pagamento viene trattenuto in garanzia e rilasciato solo a lavoro completato.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
